import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type { Category } from '../../shared/api';
import { MAX_CATEGORY_NAME_LENGTH } from '../../shared/api';
import { NewCategoryForm } from '../components/NewCategoryForm';
import { useCategories } from '../hooks/useCategories';
import { api, errorMessage } from '../lib/api';

function bookCountLink(category: Category) {
  const label = `${category.bookCount} ${category.bookCount === 1 ? 'book' : 'books'}`;
  return category.bookCount > 0 ? (
    <Link to={`/collection?category=${category.id}`}>{label}</Link>
  ) : (
    <span className="muted">{label}</span>
  );
}

export function CategoriesPage() {
  const { categories, error, reload, create } = useCategories();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function remove(category: Category) {
    const books = category.bookCount === 1 ? 'Its 1 book stays' : `Its ${category.bookCount} books stay`;
    if (!window.confirm(`Delete "${category.name}"? ${books} in your collection.`)) return;
    setActionError(null);
    try {
      await api.deleteCategory(category.id);
      reload();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  }

  if (!categories) {
    return error ? (
      <div className="page-status" role="alert">
        <p className="error-text">{errorMessage(error)}</p>
        <button type="button" className="btn btn--primary" onClick={reload}>
          Try again
        </button>
      </div>
    ) : (
      <p className="page-status">Loading categories…</p>
    );
  }

  const builtIn = categories.filter((category) => category.builtIn);
  const own = categories.filter((category) => !category.builtIn);

  return (
    <section className="categories-page">
      <Link className="back-link" to="/collection">
        ← Collection
      </Link>
      <h1>Categories</h1>
      <p className="muted">A book can be in as many categories as you like. Choose one on the scan page to sort books as you scan.</p>

      <h2>Your categories</h2>
      <p className="muted">Only you can see these, even friends who can see your collection.</p>
      <NewCategoryForm create={create} onCreated={() => setActionError(null)} />
      {actionError && (
        <p className="error-text" role="alert">
          {actionError}
        </p>
      )}
      {own.length === 0 ? (
        <p className="hint">You haven't added any yet, e.g. "Upstairs library" or "Kids' books".</p>
      ) : (
        <ul className="category-list">
          {own.map((category) =>
            editingId === category.id ? (
              <li key={category.id} className="category-list__item">
                <RenameForm
                  category={category}
                  onDone={() => {
                    setEditingId(null);
                    reload();
                  }}
                  onCancel={() => setEditingId(null)}
                />
              </li>
            ) : (
              <li key={category.id} className="category-list__item">
                <span className="category-list__name">{category.name}</span>
                {bookCountLink(category)}
                <span className="category-list__actions">
                  <button type="button" className="btn btn--ghost btn--small" onClick={() => setEditingId(category.id)}>
                    Rename
                  </button>
                  <button type="button" className="btn btn--ghost btn--small btn--danger-text" onClick={() => void remove(category)}>
                    Delete
                  </button>
                </span>
              </li>
            ),
          )}
        </ul>
      )}

      <h2>Built in</h2>
      <p className="muted">Everyone has these. Friends can see which of your books are in them.</p>
      <ul className="category-list">
        {builtIn.map((category) => (
          <li key={category.id} className="category-list__item">
            <span className="category-list__name">{category.name}</span>
            {bookCountLink(category)}
          </li>
        ))}
      </ul>
    </section>
  );
}

function RenameForm({ category, onDone, onCancel }: { category: Category; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(category.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (name.trim() === category.name) return onCancel();
    setSaving(true);
    setError(null);
    try {
      await api.renameCategory(category.id, name);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
    }
  }

  return (
    <form className="new-category" onSubmit={(event) => void handleSubmit(event)}>
      <div className="new-category__row">
        <input
          type="text"
          aria-label={`New name for ${category.name}`}
          maxLength={MAX_CATEGORY_NAME_LENGTH}
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <button type="submit" className="btn btn--primary btn--small" disabled={saving || !name.trim()}>
          Save
        </button>
        <button type="button" className="btn btn--secondary btn--small" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
